import type {
  AssessmentAssignmentResult,
  AssessmentInstrumentResult,
  AssessmentOccasionResult,
  AssessmentScoreRow,
} from '@cartwheel/api/v1'
import { useClientAssessmentResultsQuery } from '@cartwheel/api/v1'
import { Badge, DataTable, EmptyState, ErrorView, LoadingView, Select } from '@cartwheel/components'
import { useState } from 'react'
import { AssessmentTrendChart } from './assessment-trend-chart'

interface Props {
  clientId: string
  instrumentKey: string
}

const SEVERITY_TEXT_COLOR: Record<string, string> = {
  neutral: 'text-forest',
  caution: 'text-bronze',
  severe: 'text-brick-light',
}

function severityColor(level: AssessmentScoreRow['severity_level']): string {
  return level ? (SEVERITY_TEXT_COLOR[level] ?? 'text-charcoal') : 'text-charcoal'
}

function occasionLabel(occasion: AssessmentOccasionResult): string {
  if (!occasion.completed_at) return occasion.kind_label
  const date = new Date(occasion.completed_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${occasion.kind_label} · ${date}`
}

export default function ClientAssessmentResults({ clientId, instrumentKey }: Props) {
  const { data, isLoading, error } = useClientAssessmentResultsQuery({
    path: { client_id: clientId },
  })

  if (isLoading) return <LoadingView title="Loading assessment results…" className="py-20" />
  if (error)
    return <ErrorView title="Error loading assessments" message="Please refresh to try again." />

  const instrument = (data?.instruments ?? []).find(
    (candidate) => candidate.instrument_key === instrumentKey
  )
  if (!instrument) {
    return (
      <EmptyState
        title="No assessment results yet"
        description="Completed responses will appear here."
      />
    )
  }

  return (
    <div className="px-4 py-8 lg:px-8">
      <InstrumentResults instrument={instrument} />
    </div>
  )
}

function InstrumentResults({ instrument }: { instrument: AssessmentInstrumentResult }) {
  const [selectedOccasionId, setSelectedOccasionId] = useState<string | null>(null)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null)

  const occasion =
    instrument.occasions.find((candidate) => candidate.id === selectedOccasionId) ??
    instrument.occasions[0]
  const assignment: AssessmentAssignmentResult | undefined =
    occasion?.assignments.find((candidate) => candidate.id === selectedAssignmentId) ??
    occasion?.assignments[0]

  const handleOccasionChange = (id: string | null) => {
    setSelectedOccasionId(id)
    setSelectedAssignmentId(null)
  }

  if (occasion == null) {
    return (
      <EmptyState
        title="No completed occasions"
        description="This assessment has no completed responses yet."
      />
    )
  }

  return (
    <>
      <div className="mb-6 max-w-xs">
        <Select
          items={instrument.occasions.map((candidate) => ({
            value: candidate.id,
            label: occasionLabel(candidate),
          }))}
          value={occasion.id}
          onChange={handleOccasionChange}
        />
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {occasion.assignments.map((candidate) => (
          <Badge
            key={candidate.id}
            label={candidate.respondent_label ?? 'Respondent'}
            shape="pill"
            color={candidate.id === assignment?.id ? 'indigo' : undefined}
            onClick={() => setSelectedAssignmentId(candidate.id)}
          />
        ))}
      </div>

      {assignment && assignment.score_table.length > 0 && (
        <div className="mb-8 overflow-hidden rounded-lg border border-gray-stroke bg-white">
          <DataTable.Root>
            <DataTable.Header>
              <DataTable.Row>
                <DataTable.HeaderCell>Subscale</DataTable.HeaderCell>
                <DataTable.HeaderCell>Score</DataTable.HeaderCell>
                <DataTable.HeaderCell>Band</DataTable.HeaderCell>
              </DataTable.Row>
            </DataTable.Header>
            <DataTable.Body>
              {assignment.score_table.map((row) => (
                <DataTable.Row key={row.subscale_key ?? 'total'}>
                  <DataTable.Cell
                    label="Subscale"
                    className={row.total ? 'font-semibold' : undefined}
                  >
                    {row.label}
                  </DataTable.Cell>
                  <DataTable.Cell label="Score" className={row.total ? 'font-semibold' : undefined}>
                    {row.raw_score ?? '—'}
                  </DataTable.Cell>
                  <DataTable.Cell label="Band">
                    <span className={severityColor(row.severity_level)}>
                      {row.band_label ?? '—'}
                    </span>
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Root>
        </div>
      )}

      {instrument.trend.series.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-3 text-lg font-medium text-charcoal">Trend</h3>
          <AssessmentTrendChart series={instrument.trend.series} />
        </div>
      )}

      {assignment && assignment.qa_rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-stroke bg-white">
          <DataTable.Root>
            <DataTable.Header>
              <DataTable.Row>
                <DataTable.HeaderCell>Question</DataTable.HeaderCell>
                <DataTable.HeaderCell>Response</DataTable.HeaderCell>
              </DataTable.Row>
            </DataTable.Header>
            <DataTable.Body>
              {assignment.qa_rows.map((row) => (
                <DataTable.Row key={`${row.subscale_key ?? 'none'}-${row.question_text}`}>
                  <DataTable.Cell label="Question">
                    {row.question_text}
                    {row.subscale_label && (
                      <span className="ml-2 text-xs text-charcoal-50">({row.subscale_label})</span>
                    )}
                  </DataTable.Cell>
                  <DataTable.Cell label="Response">{row.answer_display ?? '—'}</DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Root>
        </div>
      )}
    </>
  )
}
